import { Head, Link, router } from "@inertiajs/react"
import { ArrowDown, ArrowUp, ArrowUpDown, ImageIcon, Plus } from "lucide-react"
import { useTranslation } from "react-i18next"

import { AllCrewBadge } from "@/components/missions/all-crew-badge"
import { MissionListActiveFilterChips } from "@/components/missions/mission-list-active-filter-chips"
import { MissionListCollabCountBadge } from "@/components/missions/mission-list-collab-count-badge"
import { MissionListFilterRail } from "@/components/missions/mission-list-filter-rail"
import { MissionListNoResults } from "@/components/missions/mission-list-no-results"
import { MissionListPaginationFooter } from "@/components/missions/mission-list-pagination-footer"
import { MissionListPendingReviewBadge } from "@/components/missions/mission-list-pending-review-badge"
import { MissionListRowActions } from "@/components/missions/mission-list-row-actions"
import { ThumbnailWithFallback } from "@/components/shared/thumbnail-with-fallback"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useMissionListUrl } from "@/hooks/use-mission-list-url"
import AppLayout from "@/layouts/app-layout"
import { COVER_ASPECT_CLASS } from "@/lib/mission-cover"
import { cn } from "@/lib/utils"
import { missionPath, newMissionPath } from "@/routes"
import type {
  MissionListSort,
  MissionListSortField,
  MissionRow,
  MissionsIndexProps,
} from "@/types/missions"

/**
 * Brand/Missions/Index — operator console for the brand mission list.
 *
 * OKS-31 — extends the OKS-29 thin Index in place: filter rail (status /
 * mission_type / deadline_window / search), sortable column headers
 * (deadline / created_at / title with `aria-sort`), per-row collab
 * metric badge + kebab quick-action menu (View / Edit / Assign), Pagy
 * 25/page pagination footer (numbered desktop / Load more mobile).
 *
 * Brand isolation: the controller's `authorized_scope(Mission.all)` is
 * the only entry point — `Missions::ListQuery` runs against it; the page
 * never sees cross-brand rows.
 *
 * 5-state matrix:
 *   - `is_empty: true` (brand has zero missions ever) → warm OKS-29
 *     `EmptyState` with "Create your first mission".
 *   - `is_empty: false` AND `pagination.count === 0` (filters returned
 *     zero) → `MissionListNoResults` panel inside the table slot.
 *   - Otherwise → table renders with rows.
 */
export default function MissionsIndex({
  missions,
  is_empty,
  filters,
  sort,
  pagination,
  filter_options,
}: MissionsIndexProps) {
  const { t } = useTranslation()
  const url = useMissionListUrl(filters, sort)

  return (
    <AppLayout>
      <Head title={t("missions.head_title")} />
      <div className="flex flex-col gap-6 p-6">
        <header className="flex items-center justify-between">
          <h1 className="text-foreground text-2xl font-semibold tracking-tight">
            {t("missions.index.title")}
          </h1>
          <Button asChild data-testid="missions-new-cta">
            <Link href={newMissionPath()}>
              <Plus className="mr-1 size-4" aria-hidden="true" />
              {t("missions.index.new_cta")}
            </Link>
          </Button>
        </header>

        {is_empty ? (
          <EmptyState />
        ) : (
          <>
            <MissionListFilterRail
              filters={filters}
              filterOptions={filter_options}
              onStatusChange={(v) => url.setFilter("status", v)}
              onMissionTypeChange={(v) => url.setFilter("mission_type", v)}
              onDeadlineWindowChange={(v) =>
                url.setFilter("deadline_window", v)
              }
              onSearchChange={(v) => url.setFilter("q", v)}
              onClearAll={url.clearAll}
            />

            <MissionListActiveFilterChips
              filters={filters}
              onResetStatus={() => url.setFilter("status", "active")}
              onResetMissionType={() => url.setFilter("mission_type", "all")}
              onResetDeadlineWindow={() =>
                url.setFilter("deadline_window", "all")
              }
              onResetSearch={() => url.setFilter("q", null)}
            />

            {pagination.count === 0 ? (
              <MissionListNoResults onClearFilters={url.clearAll} />
            ) : (
              <Table data-testid="missions-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">
                      <span className="sr-only">
                        {t("missions.index.table.cover")}
                      </span>
                    </TableHead>
                    <SortHeader
                      label={t("missions.index.table.title")}
                      field="title"
                      sort={sort}
                      onSort={url.setSort}
                    />
                    <TableHead>{t("missions.index.table.type")}</TableHead>
                    <TableHead>{t("missions.index.table.status")}</TableHead>
                    <SortHeader
                      label={t("missions.index.table.deadline")}
                      field="deadline"
                      sort={sort}
                      onSort={url.setSort}
                    />
                    <TableHead>{t("missions.index.table.collabs")}</TableHead>
                    <SortHeader
                      label={t("missions.index.table.created_at")}
                      field="created_at"
                      sort={sort}
                      onSort={url.setSort}
                    />
                    <TableHead className="text-right">
                      <span className="sr-only">
                        {t("missions.index.table.actions")}
                      </span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {missions.map((mission) => (
                    <MissionTableRow key={mission.id} mission={mission} />
                  ))}
                </TableBody>
              </Table>
            )}

            <MissionListPaginationFooter
              pagination={pagination}
              onPageChange={url.setPage}
            />
          </>
        )}
      </div>
    </AppLayout>
  )
}

function MissionTableRow({ mission }: { mission: MissionRow }) {
  const { t } = useTranslation()
  const detailHref = missionPath(mission.id)

  /**
   * OKS-464 — whole-row click handler. Mirrors `crew-list-row.tsx`'s
   * `handleRowClick`: `router.visit` (not the title `<Link>`'s native
   * anchor) is used because a `<tr>` can't itself be wrapped in an `<a>`
   * (invalid HTML) or made a stretched-link overlay the way a card can.
   *
   *   - `closest("a, button")` bails when the click landed on the title
   *     `<Link>` OR the kebab (`MissionListRowActions`) trigger `<button>`
   *     OR any of its `DropdownMenuItem`s (rendered as `<a>` inside a Base
   *     UI portal). Base UI portals content elsewhere in the real DOM, but
   *     `closest()` runs on the ACTUAL clicked element — which is itself
   *     inside that `<a>`/`<button>` — so the guard resolves correctly
   *     regardless of the portal's DOM location. No `stopPropagation()`
   *     needed inside `mission-list-row-actions.tsx`.
   *   - Modifier-key bail honours cmd/ctrl/shift/alt-click conventions
   *     (open-in-new-tab intent) — a no-op here since the `<tr>` carries
   *     no `href`, but keeps behaviour consistent with the title link.
   *   - `getSelection` bail (OKS-464, new vs. `crew-list-row`) — a
   *     click-drag to select row text collapses to a normal click's
   *     `mouseup`; if the user actually dragged a selection, the Selection
   *     is non-collapsed at click time and we skip navigation (UX Spec §1
   *     edge case).
   */
  const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    if (!(e.target instanceof Element)) return
    if (e.target.closest("a, button")) return
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    if (window.getSelection()?.isCollapsed === false) return
    router.visit(detailHref)
  }

  return (
    <TableRow
      data-testid="mission-row"
      data-mission-id={mission.id}
      onClick={handleRowClick}
      className="cursor-pointer"
    >
      {/*
        OKS-609 — leading 16:9 cover thumbnail. Decorative (`alt=""`); the
        title link below remains the row's accessible nav target, so the
        thumbnail is intentionally NOT wrapped in the mission link. The
        `bg-muted` box holds layout (fixed aspect ratio → no reflow) while
        the lazy image paints, and IS the branded fallback backdrop when
        the cover is null or fails to load.

        OKS-464 — the row itself is now a whole-row click target (see
        `handleRowClick` above); hover feedback comes free from shadcn
        `TableRow`'s default `hover:bg-muted/50`.
      */}
      <TableCell data-testid="mission-cover-cell">
        <div
          className={cn(
            COVER_ASPECT_CLASS,
            "bg-muted w-16 overflow-hidden rounded-md",
          )}
        >
          <ThumbnailWithFallback
            src={mission.cover_image_display_url}
            alt=""
            loading="lazy"
            className="size-full object-cover"
            fallback={
              <div
                data-testid="mission-cover-fallback"
                className="text-muted-foreground flex size-full items-center justify-center"
                aria-hidden="true"
              >
                <ImageIcon className="size-4" />
              </div>
            }
          />
        </div>
      </TableCell>
      <TableCell className="font-medium">
        <Link href={detailHref} className="hover:underline">
          {mission.title}
        </Link>
      </TableCell>
      <TableCell>
        <Badge variant="outline">
          {t(`missions.types.${mission.mission_type}`)}
        </Badge>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge
            variant={mission.status === "active" ? "default" : "secondary"}
            data-testid="mission-row-status-pill"
          >
            {t(`missions.statuses.${mission.status}`)}
          </Badge>
          <AllCrewBadge audience={mission.audience} />
        </div>
      </TableCell>
      {/*
        OKS-464 — plain-text, non-interactive cell used by E2E as the
        guaranteed-safe row-click hit-area (no nested link/button/portal
        content to accidentally land on).
      */}
      <TableCell data-testid="mission-row-click-target">
        {mission.deadline
          ? new Date(mission.deadline).toLocaleDateString()
          : t("missions.index.table.no_deadline")}
      </TableCell>
      <TableCell>
        {/*
          OKS-641 — collab-count badge + "to review" badge share the cell.
          `flex-wrap` lets the amber badge drop below the neutral count on a
          ≤375px viewport rather than pushing the row wider (UX Spec §5).
          Reading order (count → to-review) matches UX Spec §6.
        */}
        <div className="flex flex-wrap items-center gap-1.5">
          <MissionListCollabCountBadge collabCount={mission.collab_count} />
          <MissionListPendingReviewBadge
            pendingReviewCount={mission.pending_review_count}
          />
        </div>
      </TableCell>
      <TableCell>{new Date(mission.created_at).toLocaleDateString()}</TableCell>
      <TableCell className="text-right">
        <MissionListRowActions row={mission} />
      </TableCell>
    </TableRow>
  )
}

interface SortHeaderProps {
  label: string
  field: MissionListSortField
  sort: MissionListSort
  onSort: (next: MissionListSort) => void
}

function SortHeader({ label, field, sort, onSort }: SortHeaderProps) {
  const { t } = useTranslation()
  const isActive = sort.field === field
  const ariaSort: "ascending" | "descending" | "none" = !isActive
    ? "none"
    : sort.dir === "asc"
      ? "ascending"
      : "descending"

  // Toggle: clicking a non-active column applies the field's default
  // direction; clicking the active column flips asc↔desc.
  const onClick = () => {
    const dir =
      isActive && sort.dir === "asc"
        ? "desc"
        : isActive && sort.dir === "desc"
          ? "asc"
          : defaultDirForField(field)
    onSort({ field, dir })
  }

  return (
    <TableHead aria-sort={ariaSort}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onClick}
        className={cn(
          "-ml-3 h-8 px-3 font-medium",
          isActive && "text-foreground",
        )}
        data-testid={`mission-list-sort-${field}`}
        aria-label={
          isActive
            ? sort.dir === "asc"
              ? t("missions.index.sort.aria_asc")
              : t("missions.index.sort.aria_desc")
            : undefined
        }
      >
        {label}
        {!isActive ? (
          <ArrowUpDown className="ml-1 size-3" aria-hidden="true" />
        ) : sort.dir === "asc" ? (
          <ArrowUp className="ml-1 size-3" aria-hidden="true" />
        ) : (
          <ArrowDown className="ml-1 size-3" aria-hidden="true" />
        )}
      </Button>
    </TableHead>
  )
}

function defaultDirForField(field: MissionListSortField): "asc" | "desc" {
  switch (field) {
    case "deadline":
      return "asc"
    case "created_at":
      return "desc"
    case "title":
      return "asc"
  }
}

function EmptyState() {
  const { t } = useTranslation()
  return (
    <div
      className="border-input flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-12 text-center"
      data-testid="mission-list-empty"
    >
      <h2 className="text-foreground text-lg font-medium">
        {t("missions.index.empty.title")}
      </h2>
      <p className="text-muted-foreground max-w-md text-sm">
        {t("missions.index.empty.body")}
      </p>
      <Button asChild>
        <Link href={newMissionPath()}>
          <Plus className="mr-1 size-4" aria-hidden="true" />
          {t("missions.index.new_cta")}
        </Link>
      </Button>
    </div>
  )
}
